import React, { useEffect, useState } from 'react';
import { SolarAccelerationService, SolarAccelerationData } from '../../services/solarAccelerationService';
import { ChartRendererSupabase } from './ChartRendererSupabase';
import { ChartLayout } from './ChartLayout';
import SolarAccelerationMetricsComponent from './SolarAccelerationMetrics';

interface SolarAccelerationChartProps {
  location: string;
}

const SolarAccelerationChart: React.FC<SolarAccelerationChartProps> = ({ location }) => {
  const [data, setData] = useState<SolarAccelerationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState('overview');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await SolarAccelerationService.getData(location);
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load solar acceleration data');
      } finally {
        setLoading(false);
      }
    };

    if (location) {
      loadData();
    }
  }, [location]);

  if (loading) {
    return <div className="text-center p-8">Loading solar acceleration data...</div>;
  }

  if (error) {
    return <div className="text-center p-8 text-red-600">Error: {error}</div>;
  }

  if (!data) {
    return <div className="text-center p-8">No data available</div>;
  }

  const chartContent = (
    <ChartRendererSupabase
      chartData={data.chartData}
      chartOptions={data.chartOptions}
      height="450px"
    />
  );

  const keyMetrics = <SolarAccelerationMetricsComponent metrics={data.metrics} />;

  const overviewContent = (
    <div className="space-y-4">
      <p className="text-gray-700">{data.narrative}</p>
      <div className="bg-green-50 border-l-4 border-green-500 p-4">
        <h4 className="font-semibold text-green-900 mb-2">Key Insight</h4>
        <p className="text-green-800">
          Solar adoption is growing at a 5-year compound annual growth rate of {data.metrics.fiveYearCAGR.toFixed(1)}%.
          At this pace, {location} is projected to {data.metrics.onTrackToTarget ? 'exceed' : 'fall short of'} its 2030 target
          by {Math.abs(data.metrics.targetGapKw / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} MW.
        </p>
      </div>
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
        <h4 className="font-semibold text-blue-900 mb-2">Historical Momentum</h4>
        <p className="text-blue-800">
          The peak acceleration occurred in {data.metrics.peakGrowthYear}, with year-over-year growth of {data.metrics.peakGrowthRate.toFixed(1)}%.
          This likely reflects the impact of state incentive programs, solar mandates, and declining technology costs.
        </p>
      </div>
    </div>
  );

  const relevanceContent = (
    <div className="space-y-4">
      <h4 className="font-semibold text-gray-900">Why Growth Momentum Matters</h4>
      <p className="text-gray-700">
        Tracking solar adoption acceleration is critical for understanding whether communities are on pace to meet
        climate targets. Steady growth is not enough—accelerating adoption is necessary to achieve California's
        ambitious goals under SB 100 (100% clean energy by 2045).
      </p>

      <h4 className="font-semibold text-gray-900 mt-4">Policy Implications</h4>
      <p className="text-gray-700">
        Growth rate trends reveal the effectiveness of policy interventions such as building codes, incentive programs,
        and streamlined permitting. Identifying years with peak growth helps policymakers understand which strategies
        work best and should be expanded or replicated.
      </p>

      <h4 className="font-semibold text-gray-900 mt-4">California's SB 100 Targets</h4>
      <p className="text-gray-700">
        California's Senate Bill 100 requires 100% clean energy by 2045, with interim goals of 60% renewable electricity
        by 2030. This chart projects whether current solar adoption trends will contribute to meeting {location}'s
        proportional share of these statewide targets, scaled by population.
      </p>

      <h4 className="font-semibold text-gray-900 mt-4">Compound Annual Growth Rate (CAGR)</h4>
      <p className="text-gray-700">
        The 5-year CAGR smooths out year-to-year volatility to show the sustained growth trend. This metric is more
        reliable than single-year growth rates for projecting future capacity and assessing long-term momentum.
      </p>
    </div>
  );

  const methodologyContent = (
    <div className="space-y-4">
      <h4 className="font-semibold text-gray-900">Data Sources</h4>
      <ul className="list-disc list-inside text-gray-700 space-y-2">
        <li>Solar PV installation data from <code>all_pv_size</code> table (Lawrence Berkeley National Lab Tracking the Sun dataset)</li>
        <li>Population data from <code>population</code> table (U.S. Census Bureau)</li>
        <li>California SB 100 targets from state energy policy (CPUC/CEC)</li>
      </ul>

      <h4 className="font-semibold text-gray-900 mt-4">Calculations</h4>
      <ul className="list-disc list-inside text-gray-700 space-y-2">
        <li>
          <strong>Annual New Capacity:</strong> Total capacity installed in each year minus previous year's cumulative total (kW)
        </li>
        <li>
          <strong>Year-over-Year Growth Rate:</strong> Percentage increase in cumulative capacity from one year to the next:
          <code className="ml-2">(Current Year - Previous Year) / Previous Year × 100</code>
        </li>
        <li>
          <strong>5-Year CAGR (Compound Annual Growth Rate):</strong> Smoothed growth rate calculated over a 5-year rolling window:
          <code className="ml-2">((Current / 5 Years Ago)^(1/5) - 1) × 100</code>
        </li>
        <li>
          <strong>2030 Projection:</strong> Current capacity multiplied by projected growth using the latest 5-year CAGR:
          <code className="ml-2">Current × (1 + CAGR/100)^Years</code>
        </li>
        <li>
          <strong>2030 Target:</strong> Proportional share of California's 50 GW solar target, scaled by population:
          <code className="ml-2">(Location Pop / CA Pop) × 50 GW</code>
        </li>
      </ul>

      <h4 className="font-semibold text-gray-900 mt-4">Limitations</h4>
      <ul className="list-disc list-inside text-gray-700 space-y-2">
        <li>Projections assume growth rates remain constant, which may not reflect future policy changes or market disruptions</li>
        <li>Target calculation is a simplified proportional allocation and does not account for local geography, grid capacity, or policy goals</li>
        <li>Data reflects installed capacity, not generation (actual output depends on weather, system performance, etc.)</li>
        <li>5-year CAGR is not calculated for years without sufficient historical data (first 5 years show as zero)</li>
        <li>Does not account for system decommissioning or retirement</li>
      </ul>

      <h4 className="font-semibold text-gray-900 mt-4">Time Period</h4>
      <p className="text-gray-700">
        Data covers {data.metrics.earliestYear} to {data.metrics.latestYear}, with projections to 2030 based on recent growth trends.
      </p>
    </div>
  );

  return (
    <ChartLayout
      title="Solar Growth Momentum Analysis"
      description="Tracking solar adoption acceleration and progress toward 2030 clean energy targets"
      keyMetrics={keyMetrics}
      chartContent={chartContent}
      overviewContent={overviewContent}
      relevanceContent={relevanceContent}
      methodologyContent={methodologyContent}
      activeView={activeView}
      onViewChange={setActiveView}
      layout="side-by-side"
    />
  );
};

export default SolarAccelerationChart;
